import graphql__da04716605db4bf3d289 from "./__generated__/EditSubscriptionReasonDialogMutation.graphql.ts";import * as React from "react";
import {useContext} from "react";
import {graphql} from "react-relay";
import Section from "../form/Section";
import Field from "../form/Field";
import Row from "../form/Row";
import Label from "../form/Label";
import * as yup from "yup";
import FormDialog from "../dialog/FormDialog";
import {useRelayEnvironment} from "react-relay/hooks";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import {useIntl} from "react-intl";
import CabanaConfigurationContext from "../../configuration/CabanaConfigurationContext";
import type {EditSubscriptionReasonDialogMutation} from "./__generated__/EditSubscriptionReasonDialogMutation.graphql";

type Props = {
    mailingListId: string
    mailingListName: string
    currentSubscriptionReason: { de: string | null, en: string | null } | null
    handleCloseDialog: () => void
}

const EditSubscriptionReasonDialog: React.FC<Props> = ({
    mailingListId,
    mailingListName,
    currentSubscriptionReason,
    handleCloseDialog
}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const configuration = useContext(CabanaConfigurationContext);

    const supportedLanguages = configuration.languages?.supported || ['de', 'en'];
    const languageLabels = configuration.languages?.labels || { de: 'Deutsch', en: 'English' };

    // Default subscription reason texts (matching the old static texts)
    const defaultTexts: Record<string, string> = {
        de: intl.formatMessage({id: 'mailingList.subscriptionReason.defaultText.de'}, {listName: mailingListName}),
        en: intl.formatMessage({id: 'mailingList.subscriptionReason.defaultText.en'}, {listName: mailingListName}),
    };

    const handleSubmission = (input: Record<string, string>) => {
        commitMutationWithErrorHandling<EditSubscriptionReasonDialogMutation>(
            relayEnvironment,
            {
                mutation: graphql__da04716605db4bf3d289,
                variables: {
                    input: {
                        id: mailingListId,
                        de: input.de,
                        en: input.en,
                    }
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    handleCloseDialog();
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToUpdateSubscriptionReason'})
            }
        )
    }

    // Build initial values from current subscription reason or empty strings
    const initialValues: Record<string, string> = {};
    supportedLanguages.forEach(lang => {
        initialValues[lang] = (currentSubscriptionReason as any)?.[lang] || '';
    });

    // Build validation schema for all supported languages
    const schemaFields: Record<string, yup.StringSchema> = {};
    supportedLanguages.forEach(lang => {
        schemaFields[lang] = yup.string()
            .max(255, intl.formatMessage({id: 'validation.field.descriptionTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'}));
    });
    const validationSchema = yup.object().shape(schemaFields);

    return (
        <FormDialog
            handleCloseDialog={handleCloseDialog}
            handleSubmission={handleSubmission}
            initialValues={initialValues}
            validationSchema={validationSchema}
            headline={intl.formatMessage({id: 'dialog.EditSubscriptionReasonDialog.headline'})}
            submitLabel={intl.formatMessage({id: 'button.save'})}
        >
            <Section>
                {supportedLanguages.map(lang => (
                    <Row key={lang}>
                        <Label>{languageLabels[lang]}</Label>
                        <Field
                            name={lang}
                            type="text"
                            placeholder={defaultTexts[lang]}
                        />
                    </Row>
                ))}
            </Section>
        </FormDialog>
    )
}

export default EditSubscriptionReasonDialog;
